import { Link } from 'react-router-dom';
import { useProjectRefs } from '@/lib/useProjectRefs';

/**
 * Projekt-Anzeige: **Nummer und Name gehören zusammen** (Wunsch Tech-Lead
 * 2026-07-28 — eine Nummer allein sagt niemandem etwas). Ein Baustein für die
 * ganze App, damit es überall gleich aussieht.
 *
 * Der Name kommt entweder aus dem Datensatz selbst (Snapshot am Beleg — dann ist
 * er historisch korrekt) oder, wo nur die Nummer gespeichert ist, aus dem
 * Nachschlagewerk `useProjectRefs()`. Ist er nirgends zu holen, steht die Nummer
 * allein da: nie ein Platzhalter, nie ein Sprung im Layout.
 */
export function ProjectRef({
  projectId,
  code,
  name,
  className,
  linkTo = true,
  fallback = '—',
}: {
  projectId?: string | null;
  code?: string | null;
  /** Am Datensatz gespeicherter Name (Snapshot). Fehlt er, wird nachgeschlagen. */
  name?: string | null;
  className?: string;
  /** Als Link auf das Projekt rendern (braucht `projectId`). */
  linkTo?: boolean;
  /** Anzeige, wenn kein Projekt gesetzt ist. */
  fallback?: string;
}) {
  const refs = useProjectRefs();
  const resolved = projectId ? refs.get(projectId) : undefined;
  const shownCode = code ?? resolved?.code ?? null;
  const shownName = name ?? resolved?.name ?? null;

  if (!shownCode && !shownName) return <span className="text-muted-foreground">{fallback}</span>;

  const label = projectRefLabel(shownCode, shownName);
  const body = (
    <>
      {shownCode && <span className="font-mono text-xs">{shownCode}</span>}
      {shownName && <span className={shownCode ? 'ml-1.5' : undefined}>{shownName}</span>}
    </>
  );

  if (linkTo && projectId) {
    return (
      <Link to={`/projekte/${projectId}`} className={`hover:underline ${className ?? ''}`} title={label}>
        {body}
      </Link>
    );
  }
  return (
    <span className={className} title={label}>
      {body}
    </span>
  );
}

/**
 * Textform für Stellen ohne eigene Zeile (Untertitel, Tooltip, Export):
 * „P-2026-001 · Kläranlage Wien", oder nur eines von beidem, wenn das andere fehlt.
 */
export function projectRefLabel(code?: string | null, name?: string | null): string {
  return [code, name].filter(Boolean).join(' · ');
}
